import React, { useState, useEffect } from "react";
import useFetch from "../../customHooks/useFetch";
import { useLocation } from "react-router-dom";
import { convertFromJson } from "fiql-query-builder";
import { useOidcUser, useOidc } from "@axa-fr/react-oidc";
import { toast } from "react-toastify";
import BreadcrumbItem from "../../Components/common/Breadcrumb";
import {
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Modal,
  ModalBody,
  ModalHeader,
  Button,
} from "reactstrap";
import TableAdvanced from "../../Components/common/tableAdvanced/TableAdvanced";
import Create from "../Addresses/create";
import Edit from "../Addresses/edit";
import Delete from "../Addresses/delete";
import CompaniesService from "../../services/registry-service/companies";
import {
  getUniqueSelectors,
  writeLabelAddress,
  buildQuerySort,
  callErrorToast,
} from "../../utilities";
import { useAppContext } from "../../AppProvider";

const companiesService = new CompaniesService();

const modalSettingsDefault = {
  open: false,
  title: "",
  icon: "",
  id: null,
  action: "",
};

let defaultQuerySearch = [
  {
    equals: {
      selector: "myAddress",
      args: "false",
    },
  },
];

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}
const AddressBook = () => {
  const query = useQuery();
  const { oidcUser } = useOidcUser();
  const { dictionary } = useAppContext();

  //Se presente un companyId in query string, queryParams è uguale alla concatenazione di defaultQueryString ed un array contenente
  //l'oggetto di ricerca relativo alla companyId
  const [queryParams, setQueryParams] = useState([
    ...defaultQuerySearch,
    ...(query.get("companyId")
      ? [
          {
            equals: {
              selector: "ownerId",
              args: query.get("companyId"),
            },
          },
        ]
      : []),
  ]);
  const [querySearch, setQuerySearch] = useState();
  const [fetchWait, setFetchWait] = useState(true);
  const [company, setCompany] = useState({});
  const [modalSettings, setModalSettings] = useState({ modalSettingsDefault });
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
  });
  const [sort, setSort] = useState([
    {
      orderBy: "asc",
      column: "name",
    },
  ]);

  const columns = [
    {
      Header: dictionary.registries.name,
      accessor: "name",
      sortable: true,
      filter: {
        active: true,
        name: "name",
        type: "search",
      },
    },
    {
      Header: dictionary.locations.address,
      accessor: "location",
      Cell: ({ row, cell }) => {
        return writeLabelAddress(cell.value);
      },
    },
    {
      Header: dictionary.registries.email,
      accessor: "email",
    },
    {
      Header: dictionary.registries.phone,
      accessor: "phone",
    },
    // {
    //   Header: "Cellulare",
    //   accessor: "mobilePhone",
    // },
    {
      Header: dictionary.registries.private,
      accessor: "priv",
      Cell: ({ row }) => {
        return row.original.priv ? "SI" : "NO";
      },
    },
    {
      Header: "GDO",
      accessor: "gdo",
      Cell: ({ row }) => {
        return row.original.gdo ? "SI" : "NO";
      },
    },
    {
      Header: "ZTL",
      accessor: "ztl",
      Cell: ({ row }) => {
        return row.original.ztl ? "SI" : "NO";
      },
    },
    {
      Header: dictionary.words.actions,
      pinning: "right",
      accessor: "id",
      Cell: ({ row }) => {
        const { values } = row;
        return (
          <UncontrolledDropdown>
            <DropdownToggle
              role="button"
              tag="button"
              type="button"
              href="#"
              className="btn border-0 text-body"
              data-bs-toggle="dropdown"
            >
              <i className="ph-list"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end" container={"body"}>
              <DropdownItem
                href="#"
                onClick={() => {
                  setModalSettings({
                    ...modalSettings,
                    title: dictionary.actions.edit,
                    icon: "ph-note-pencil",
                    open: !modalSettings.open,
                    action: "edit",
                    id: values.id,
                    entity: "destination",
                  });
                }}
              >
                <i className="ph-note-pencil me-2"></i>
                {dictionary.actions.edit}
              </DropdownItem>
              <DropdownItem
                className="text-danger"
                href="#"
                onClick={() => {
                  setModalSettings({
                    ...modalSettings,
                    title: dictionary.actions.delete,
                    icon: "ph-trash",
                    open: !modalSettings.open,
                    action: "delete",
                    id: values.id,
                    entity: "destination",
                  });
                }}
              >
                <i className="ph-trash me-2"></i>
                {dictionary.actions.delete}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  const getCompany = () => {
    companiesService
      .getCompany(query.get("companyId"))
      .then((response) => {
        setCompany(response);
      })
      .catch((err) => {
        callErrorToast(err, dictionary);
      });
  };

  const modalToggle = () => {
    setModalSettings({ modalSettingsDefault, open: !modalSettings.open });
  };

  const handlePagination = (paginationOpt) => {
    setPagination({ ...pagination, ...paginationOpt });
  };

  const handleSorting = (sortingOpt) => {
    setSort((sort) => {
      const sortIndex = sort.findIndex((x) => x.column === sortingOpt.column);
      let newSort = [...sort];
      if (sortIndex !== -1) {
        newSort[sortIndex] = sortingOpt;
      } else {
        newSort = [];
        newSort.push(sortingOpt);
      }

      return newSort;
    });
  };

  const buildQuerySearch = (query = []) => {
    return encodeURIComponent(convertFromJson({ and: [...query] }));
  };

  const onSearch = (selector, value, preciseSearch = false) => {
    setQueryParams((queryParams) => {
      return getUniqueSelectors(selector, value, preciseSearch, queryParams);
    });
    handlePagination({ page: 0 });
  };

  const { data, loading, error, refreshData } = useFetch(
    `/registry-service/address-book?page=${pagination.page}&size=${
      pagination.size
    }${sort.length > 0 ? buildQuerySort(sort) : ""}${
      queryParams.length > 0 ? `&search=${buildQuerySearch(queryParams)}` : ""
    }`,
    {},
    fetchWait
  );

  useEffect(() => {
    if (query.get("companyId")) {
      setFetchWait(false);
      getCompany();
    }
  }, []);

  useEffect(() => {
    if (!modalSettings.open) refreshData(fetchWait);
  }, [modalSettings]);

  useEffect(() => {
    setQuerySearch(buildQuerySearch(queryParams));
  }, [queryParams, querySearch, pagination, sort]);

  useEffect(() => {
    //Se nell'url è presente companyId allora non setto param di ricerca con mio ownerId (oidcUser)
    if (!query.get("companyId") && oidcUser) {
      setQueryParams([
        ...defaultQuerySearch,
        {
          equals: {
            selector: "ownerId",
            args: oidcUser.owner,
          },
        },
      ]);
      setFetchWait(false);
    }
  }, [oidcUser]);

  return (
    <React.Fragment>
      {window.location.pathname === "/layout_no_header" ||
      window.location.pathname === "/layout_fixed_header" ? (
        ""
      ) : (
        <BreadcrumbItem
          title={dictionary.registries.registries}
          breadcrumbItem={
            company?.name || dictionary.contacts.destinations_address_book
          }
        />
      )}

      <div className="content" id="container">
        <Card>
          <CardBody>
            <div className="row">
              <div className="col">
                <Button
                  color="light"
                  type="button"
                  className="btn btn-outline-success btn-sm float-end"
                  onClick={() =>
                    setModalSettings({
                      ...modalSettings,
                      title: `${dictionary.words.new_sf} ${dictionary.registries.destination}`,
                      icon: "ph-plus",
                      open: !modalSettings.open,
                      action: "create",
                      entity: "destination",
                    })
                  }
                >
                  {dictionary.words.new_sf} {dictionary.registries.destination}{" "}
                  <i className="ph-plus ms-2"></i>
                </Button>
              </div>
            </div>

            {oidcUser && (
              <TableAdvanced
                hasFilter={true}
                columns={columns}
                data={data}
                className="custom-header-css"
                tableClass={"table-xxs"}
                paginationOpt={pagination}
                onPaginationChange={handlePagination}
                sortingOpt={sort}
                onSortingChange={handleSorting}
                onSearch={onSearch}
                showLoader={loading}
              />
            )}

            <Modal isOpen={modalSettings.open} className="modal-content">
              <ModalHeader toggle={modalToggle}>
                <span className="modal-title">
                  <i className={modalSettings.icon + " me-2"}></i>
                  {modalSettings.title}
                </span>
              </ModalHeader>
              <ModalBody>
                {modalSettings.action === "create" && (
                  <Create
                    companyId={company?.id || oidcUser.owner}
                    modalToogle={modalToggle}
                    refreshTable={refreshData}
                    params={{
                      companyId: company?.id || oidcUser.owner,
                      myAddress: false,
                    }}
                  />
                )}
                {modalSettings.action === "edit" && (
                  <Edit
                    addressId={modalSettings.id}
                    params={{
                      companyId: company?.id || oidcUser.owner,
                      myAddress: false,
                    }}
                    modalToogle={modalToggle}
                    refreshTable={refreshData}
                  />
                )}
                {modalSettings.action === "delete" && (
                  <Delete
                    addressBookId={modalSettings.id}
                    modalToogle={modalToggle}
                    refreshTable={refreshData}
                    entity={modalSettings.entity}
                  />
                )}
              </ModalBody>
            </Modal>
          </CardBody>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default AddressBook;
